import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMembersQuery } from '@/api/query/user';
import { useAppContext } from '@/contexts/AppContext';
import type { Member } from '@/types';
import { Baby, Download, FileCheck, HeartCrack, Plus, Search, UserPlus, Users } from 'lucide-react';
import { SummaryCard } from '@/components/SummaryCard';
import { MemberTable } from './MemberTable';
import { useDrawers } from '@/contexts/DrawerContext';
import { UpsertMember } from './UpsertMember';
import { useTableFirstScroll } from '@/hooks/useTableFirstScroll';
import { useTableQuery } from '@/hooks/useTableQuery';
import { nextSortDirection } from '@/components/DataTable';
import { createColumns, type ColumnSort, type SortField } from './TableColumns';
import { displayMemberNo } from '@/lib/utils';
import { EMPTY_MONTH_RANGE, type MonthRange } from '@/components/ColumnFilter';

export default function MemberManagement() {
  const { pageRef, tableLocked } = useTableFirstScroll();
  const { isAuthenticated } = useAppContext();
  const { searchTerm, setSearchTerm, page, setPage, pageSize, setPageSize, pageQuery } = useTableQuery('all');
  const { data, isLoading, isFetching, error } = useMembersQuery(pageQuery, isAuthenticated);
  const members = useMemo(() => data?.members ?? [], [data]);
  const overview = data?.overview;
  // '--' while loading or until the backend sends the summary.
  const count = (value?: number) => (isLoading || value === undefined ? '--' : String(value));
  const { openDrawer } = useDrawers();
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  // ID sort: null = the order the API sends (also after a reload).
  const [sort, setSort] = useState<ColumnSort | null>(null);

  // Joined From / To picker: UI only for now, the rows aren't filtered by it.
  const [joinedRange, setJoinedRange] = useState<MonthRange>(EMPTY_MONTH_RANGE);

  // Life Status header choice: UI only for now, the rows aren't filtered by it.
  const [lifeStatus, setLifeStatus] = useState('');

  // Sort by ID, the only sortable column.
  const sortedMembers = useMemo(() => {
    if (!sort) return members;
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...members].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }) * direction);
  }, [members, sort]);

  const handleSort = useCallback((field: SortField) => {
    setSort((current) => {
      const direction = nextSortDirection(current?.field === field ? current.direction : null);
      return direction ? { field, direction } : null;
    });
  }, []);

  const handleViewMember = useCallback((member: Member) => openDrawer('viewMember', member), [openDrawer]);
  const handleEditMember = useCallback((member: Member) => openDrawer('editMember', member), [openDrawer]);
  const handleAddPolicy = useCallback((member: Member) => openDrawer('addPolicy', member), [openDrawer]);
  const handleTransactions = useCallback((member: Member) => openDrawer('memberTransactions', member), [openDrawer]);

  const exportMembers = useCallback(() => {
    // Simple CSV export simulation
    const csvData = [
      ['ID', 'Card No', 'Name', 'Phone', 'Aadhaar No', 'Email', 'Joined Date', 'Address'],
      ...sortedMembers.map(member => [
        displayMemberNo(member),
        member.cardNo ?? '',
        member.name,
        member.phone,
        member.aadhaarNo,
        member.email,
        member.joinedDate,
        member.address
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'members_report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }, [sortedMembers]);

  return (
    <div ref={pageRef} data-table-locked={tableLocked} className="group/page flex h-full flex-col overflow-y-auto p-5">
      {/* Header */}
      <div className="mb-6 flex-shrink-0">
        <div className="mb-4 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">Member Management</h1>
              {isFetching && !isLoading && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#4369a0]/25 border-t-[#4369a0]" />
              )}
            </div>
            <p className="mt-0.5 text-sm text-gray-500">Manage all registered members</p>
          </div>
          <Button 
            onClick={() => setIsAddMemberOpen(true)}
            className="bg-[#4369a0] text-white hover:bg-[#4369a0]/90"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Member
          </Button>
        </div>
        <div className="h-px bg-gray-200/80" />
      </div>

      {/* Summary cards: counts for all members, from the members API. */}
      <div className="mb-5 grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <SummaryCard
          icon={Users}
          tone="gray"
          label="Total Members"
          value={count(overview?.totalMembers)}
          rows={[['Male', count(overview?.male)], ['Female', count(overview?.female)]]}
        />
        <SummaryCard
          icon={UserPlus}
          tone="brand"
          label="Joined This Month"
          value={count(overview?.joinedThisMonth)}
          rows={[['Last Month', count(overview?.joinedLastMonth)], ['This Year', count(overview?.joinedThisYear)]]}
        />
        <SummaryCard
          icon={FileCheck}
          tone="emerald"
          label="With Policy"
          value={count(overview?.withPolicy)}
          rows={[['Policies', count(overview?.activePolicies)], ['No Policy', count(overview?.withoutPolicy)]]}
        />
        <SummaryCard
          icon={Baby}
          tone="teal"
          label="Age Groups"
          value={count(overview?.totalMembers)}
          rows={[['Under 5', count(overview?.under5)], ['60 & Above', count(overview?.age60AndAbove)]]}
        />
        <SummaryCard
          icon={HeartCrack}
          tone="amber"
          label="Deceased"
          value={count(overview?.deceased)}
          rows={[['Alive', count(overview?.alive)], ['Claims Settled', count(overview?.claimsSettled)]]}
        />
      </div>

      {/* Members Table */}
      <Card className="flex h-full shrink-0 flex-col border-gray-200/80 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-5">
          <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-3">
            <h2 className="whitespace-nowrap text-sm font-semibold text-gray-900">
              Members
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {data?.total ?? 0}
              </span>
            </h2>
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by ID, card no, name, phone or Aadhaar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 border-gray-200 bg-white pl-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={exportMembers}
              className="h-9 whitespace-nowrap border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-gray-200/80 bg-white">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                <p className="mt-3 text-sm text-gray-500">Loading members...</p>
              </div>
            ) : error ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50/60 px-6 text-center">
                <p className="text-sm font-medium text-red-700">Unable to load members. Please try again.</p>
              </div>
            ) : (
              <MemberTable
                members={sortedMembers}
                columns={createColumns(sort, handleSort, handleViewMember, handleEditMember, handleAddPolicy, handleTransactions, {
                  range: joinedRange,
                  onChange: setJoinedRange,
                }, {
                  value: lifeStatus,
                  onChange: setLifeStatus,
                })}
                sort={sort}
                onSort={handleSort}
                currentPage={page}
                pageSize={pageSize}
                totalItems={data?.total ?? 0}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <UpsertMember member={null} open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen} />
    </div>
  );
}

